import React, { useContext, useState } from 'react'

import { Form, Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import { CriteriaAvailability } from '@s4l/stayforlong/booking/CriteriaAvailability'
import type { Availability } from '@s4l/stayforlong/booking/Availability'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

import { RoomConfiguratorContext } from '../context'
import RadioSection from './RadioSection'
import Footer from './Footer'
import Info from './Info'
import RadioItem from './RadioItem'
import NoAvailability from '../NoAvailability'
import Placeholder from '../Placeholder'

import styles from './styles.module.css'
import type { PaymentPolicies } from '@s4l/stayforlong/booking/PaymentPolicy'
import type { Currencies } from '@s4l/stayforlong/kernel/Currency'
import { PriceType } from '@s4l/stayforlong/kernel/PriceType'
import type { UtilityClassNames } from '@s4l/utility-css'

interface IRoomConfiguratorProps {
  onSelectAvailability: (availability: Availability) => void
  onChange?: ({
    kind,
    availability,
  }: {
    kind: 'rate' | 'mealPlan'
    availability: Availability
  }) => void
}

function RoomConfiguratorForm({
  onSelectAvailability,
  onChange,
}: Readonly<IRoomConfiguratorProps>) {
  const { listOfAvailability, roomID } = useContext(RoomConfiguratorContext)

  const {
    data: market,
    loading: loadingMarket,
    error: errorMarket,
  } = useUseCase('GetCurrentMarketSharedUseCase')

  const {
    data: affiliate,
    loading: isLoadingAffiliate,
    error: errorAffiliate,
  } = useUseCase('GetCurrentAffiliateSharedUseCase')

  const listOfBoards = listOfAvailability.getListOfBoardByRoomID({ roomID }).unique()
  const listOfRates = listOfAvailability.getListOfRatesByRoomID({ roomID }).unique()

  const [availabilityMatch, setAvailabilityMatch] = useState(() => {
    const fistAvailability = listOfAvailability.at(0)
    if (!fistAvailability) return null

    return listOfAvailability.findAvailabilityByCriteria({
      criteria: CriteriaAvailability.fromLiterals({
        paymentPolicy: fistAvailability.paymentPolicy(),
        cancellationPolicyFromDate: fistAvailability.cancellationPolicyFromDate(),
        cancellationPolicyAmount: fistAvailability.cancellationPolicyAmount(),
        currency: fistAvailability.currency(),
        boardCode: fistAvailability.boardCode(),
      }),
    })
  })

  const { i18n } = useS4LContext()

  const handleChange = ({ rate, mealPlan }: { rate: string; mealPlan: string }) => {
    const [paymentPolicy, cancellationPolicyFromDate, cancellationPolicyAmount] = rate.split(
      '_'
    ) as [PaymentPolicies, string, string]

    const matchingRate = listOfRates.find(r => {
      const cancellationPolicyAmountValue = cancellationPolicyAmount
        ? Number(cancellationPolicyAmount)
        : undefined

      return (
        r.paymentPolicy() === paymentPolicy &&
        r.cancellationPolicyFromDate() === cancellationPolicyFromDate &&
        r.cancellationPolicyAmount() === cancellationPolicyAmountValue
      )
    })

    if (!matchingRate) return

    const criteria = CriteriaAvailability.fromLiterals({
      paymentPolicy: matchingRate.paymentPolicy(),
      cancellationPolicyFromDate: matchingRate.cancellationPolicyFromDate(),
      cancellationPolicyAmount: matchingRate.cancellationPolicyAmount(),
      currency: matchingRate.currency(),
      boardCode: mealPlan,
    })
    const availability = listOfAvailability.findAvailabilityByCriteria({ criteria })

    if (availability) {
      setAvailabilityMatch(availability)

      const changeKind = availabilityMatch?.boardCode() !== mealPlan ? 'mealPlan' : 'rate'
      onChange?.({ kind: changeKind, availability })
    }
  }

  // Show placeholder when loading data
  if (loadingMarket || isLoadingAffiliate) return <Placeholder />

  // Show no availability when no data is available
  if (!availabilityMatch || errorMarket || !market || errorAffiliate || !affiliate) {
    return <NoAvailability />
  }

  const priceType = PriceType.fromAffiliatePriceType({
    affiliate,
    market,
  })

  const currentMatchingAvailabilityValues = {
    rate: `${availabilityMatch.paymentPolicy()}_${availabilityMatch.cancellationPolicyFromDate()}_${availabilityMatch.cancellationPolicyAmount() || ''}`,
    mealPlan: availabilityMatch.boardCode(),
  }

  const getDiff = ({
    paymentPolicy,
    cancellationPolicyFromDate,
    cancellationPolicyAmount,
    currency,
    boardCode,
  }: {
    paymentPolicy: PaymentPolicies
    cancellationPolicyFromDate: string
    cancellationPolicyAmount: number | undefined
    currency: Currencies
    boardCode: string
  }) => {
    const criteria = CriteriaAvailability.fromLiterals({
      paymentPolicy,
      cancellationPolicyFromDate,
      cancellationPolicyAmount,
      currency,
      boardCode,
    })

    const availability = listOfAvailability.findAvailabilityByCriteria({ criteria })

    if (!availability || !availabilityMatch) return { diff: null }

    const diff = availability.priceDiff(availabilityMatch.price())

    return {
      diff: diff.getDiff(),
    }
  }

  const handleSubmit = () => {
    if (availabilityMatch) {
      onSelectAvailability(availabilityMatch)
    }
  }

  return (
    <div className={styles.container}>
      <Form
        onChange={handleChange}
        defaultValue={currentMatchingAvailabilityValues}
        onSubmit={handleSubmit}
      >
        <main className={styles.main}>
          <RadioSection
            label={i18n.formatMessage({ id: 'filter-category.payment-title' })}
            info={<Info model={availabilityMatch.rate()} />}
            options={listOfRates}
            perNight={priceType.isFeaturedPricePerNight()}
          >
            {rate => {
              const data = getDiff({
                paymentPolicy: rate.paymentPolicy(),
                cancellationPolicyFromDate: rate?.cancellationPolicyFromDate() || '',
                cancellationPolicyAmount: rate?.cancellationPolicyAmount(),
                currency: availabilityMatch.currency(),
                boardCode: availabilityMatch.boardCode(),
              })

              if (!data) return null

              let label = null

              if (rate.isNRF()) {
                label = (
                  <ul>
                    <Typography variant="body" size="l" as="li">
                      {i18n.formatMessage({ id: 'global.pay-now-full-amount' })}
                    </Typography>
                    <Typography variant="body" size="l" bold as="li">
                      {i18n.formatMessage({ id: 'reservation.no-refundable' })}
                    </Typography>
                  </ul>
                )
              }

              if (rate.isSFL()) {
                label = (
                  <ul>
                    <Typography variant="body" size="l" as="li">
                      {i18n.formatMessage(
                        { id: 'room.pay-partial' },
                        {
                          pay_rate: rate.cancellationPriceFormatted({
                            locale: i18n.locale,
                            market,
                          }),
                        }
                      )}
                      &nbsp;({i18n.formatMessage({ id: 'policies.deposit' })})
                    </Typography>
                    <Typography
                      variant="body"
                      size="l"
                      bold
                      as="li"
                      className={styles.featured as UtilityClassNames}
                    >
                      {i18n.formatMessage(
                        { id: 'policies.pay-the-rest-on' },
                        { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                      )}
                    </Typography>
                    <Typography
                      variant="body"
                      size="l"
                      as="p"
                      bold
                      className={styles.featured as UtilityClassNames}
                    >
                      {i18n.formatMessage(
                        { id: 'policies.free-cancellation-before' },
                        { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                      )}
                    </Typography>
                  </ul>
                )
              }

              if (rate.isNOR()) {
                label = (
                  <ul>
                    <Typography variant="body" size="l" as="li">
                      {i18n.formatMessage(
                        { id: 'policies.pay-on' },
                        { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                      )}
                    </Typography>

                    <Typography
                      variant="body"
                      size="l"
                      as="p"
                      bold
                      className={styles.featured as UtilityClassNames}
                    >
                      {i18n.formatMessage(
                        { id: 'policies.free-cancellation-before' },
                        { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                      )}
                    </Typography>
                  </ul>
                )
              }

              if (rate.isNORE()) {
                label = (
                  <ul>
                    <Typography variant="body" size="l" as="li">
                      {i18n.formatMessage({ id: 'global.pay-now-full-amount' })}
                    </Typography>
                    <Typography
                      variant="body"
                      size="l"
                      as="li"
                      bold
                      className={styles.featured as UtilityClassNames}
                    >
                      {i18n.formatMessage(
                        { id: 'policies.fully-refundable-until' },
                        { date: rate.cancellationDateFormatted({ locale: i18n.locale }) }
                      )}
                    </Typography>
                  </ul>
                )
              }

              const value = `${rate.paymentPolicy()}_${rate.cancellationPolicyFromDate() || ''}_${rate.cancellationPolicyAmount() || ''}`

              return (
                <RadioItem key={value} name="rate" value={value} label={label} diff={data.diff} />
              )
            }}
          </RadioSection>

          <RadioSection
            label={i18n.formatMessage({ id: 'filter-category.meal-plan' })}
            info={<Info model={availabilityMatch.board()} />}
            options={listOfBoards}
            perNight={priceType.isFeaturedPricePerNight()}
          >
            {board => {
              const boardDescription = board.shortDescription()

              if (!boardDescription) return null
              const data = getDiff({
                paymentPolicy: availabilityMatch.paymentPolicy(),
                cancellationPolicyFromDate: availabilityMatch.cancellationPolicyFromDate(),
                cancellationPolicyAmount: availabilityMatch.cancellationPolicyAmount(),
                currency: availabilityMatch.currency(),
                boardCode: board.code(),
              })

              // eslint-disable-next-line s4l/require-i18n-formatmessage-id-literal
              const label = i18n.formatMessage({ id: boardDescription })

              return (
                <RadioItem
                  key={board.code()}
                  name="mealPlan"
                  value={board.code()}
                  label={label}
                  diff={data.diff}
                />
              )
            }}
          </RadioSection>
        </main>
        <Footer availability={availabilityMatch} disabled={!availabilityMatch} />
      </Form>
    </div>
  )
}

RoomConfiguratorForm.displayName = 'RoomConfigurator.Form'
RoomConfiguratorForm.Placeholder = Placeholder

export default RoomConfiguratorForm
